import { createContext, useContext, useMemo, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface PlatformStatus {
  purchasesDisabled: boolean;
  maintenanceMode: boolean;
  comingSoonMode: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
}

const PlatformStatusContext = createContext<PlatformStatus>({
  purchasesDisabled: false,
  maintenanceMode: false,
  comingSoonMode: false,
  loading: true,
  refresh: async () => {},
});

export const usePlatformStatus = () => useContext(PlatformStatusContext);

interface StatusFlags {
  purchasesDisabled: boolean;
  maintenanceMode: boolean;
  comingSoonMode: boolean;
}

export function PlatformStatusProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<StatusFlags>({
    queryKey: ['admin-settings'],
    queryFn: async () => {
      const { data } = await supabase
        .from('admin_settings')
        .select('key, value')
        .in('key', ['purchases_disabled', 'maintenance_mode', 'coming_soon_mode']);

      const flags: StatusFlags = {
        purchasesDisabled: false,
        maintenanceMode: false,
        comingSoonMode: false,
      };
      data?.forEach((row) => {
        if (row.key === 'purchases_disabled') flags.purchasesDisabled = row.value === 'true';
        if (row.key === 'maintenance_mode') flags.maintenanceMode = row.value === 'true';
        if (row.key === 'coming_soon_mode') flags.comingSoonMode = row.value === 'true';
      });
      return flags;
    },
    refetchInterval: 30_000,
    staleTime: 25_000,
  });

  const value = useMemo<PlatformStatus>(
    () => ({
      purchasesDisabled: data?.purchasesDisabled ?? false,
      maintenanceMode: data?.maintenanceMode ?? false,
      comingSoonMode: data?.comingSoonMode ?? false,
      loading: isLoading,
      refresh: async () => {
        await queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      },
    }),
    [data, isLoading, queryClient]
  );

  return (
    <PlatformStatusContext.Provider value={value}>
      {children}
    </PlatformStatusContext.Provider>
  );
}
